import {Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {HttpClient} from '@angular/common/http';
import {environment} from '@env/environment';
import {CashRegister, CashRegisterActiveStatus} from '../domain/cash-register';
import {CashRegisterDetail} from '@app/domain/cash-register-detail';
import {CashRegisterDetailTracking} from '@app/domain/cash-register-detail-tracking';
import {DomSanitizer} from '@angular/platform-browser';
import {Page} from '@app/domain/page';
import {TotalMovimientos} from '@app/domain/total-movimientos';
import {promiseBlob2Data} from '@app/utils/util-functions';
import {CoffeEscPosService} from '@app/service/coffe-esc-pos.service';

@Injectable({
    providedIn: 'root'
})
export class CashRegisterService {

    baseUrl: string = environment.endpointApi;

    constructor(private http: HttpClient, private sanitizer: DomSanitizer,
                private coffeEscPosService: CoffeEscPosService) {
    }

    findAll(params: string): Observable<any> {
        return this.http.get<any>(`${this.baseUrl}/cash-register?${params}`);
    }

    findActiveStatus(idPuntoVenta: number, idEstacionVenta: number): Observable<CashRegisterActiveStatus> {
        return this.http.get<CashRegisterActiveStatus>(`${this.baseUrl}/cash-register/active-status`, {
            params: {idPuntoVenta, idEstacionVenta},
            headers: {hideLoader: 'true'}
        });
    }

    aperturar(model: CashRegister): Observable<CashRegister> {
        return this.http.post<CashRegister>(`${this.baseUrl}/cash-register`, model);
    }

    closeCashRegister(id, model: any): Observable<CashRegister> {
        return this.http.patch<CashRegister>(`${this.baseUrl}/cash-register/operations/close?id=${id}`, model);
    }

    getCashRegisgerDetail(id): Observable<CashRegisterDetail> {
        return this.http.get<CashRegisterDetail>(`${this.baseUrl}/cash-register-detail/${id}`);
    }

    saveCashRegisgerDetail(model: any): Observable<CashRegister> {
        return this.http.post<CashRegister>(`${this.baseUrl}/cash-register-detail`, model);
    }

    updateCashRegisgerDetail(model: any): Observable<CashRegisterDetail> {
        return this.http.put<CashRegisterDetail>(`${this.baseUrl}/cash-register-detail`, model);
    }

    findAllDetails(params: any): Observable<CashRegisterDetail[]> {
        return this.http.get<CashRegisterDetail[]>(`${this.baseUrl}/cash-register-detail?${params}`);
    }

    findAllDetailsPage(params: any): Observable<Page<CashRegisterDetail>> {
        return this.http.get<Page<CashRegisterDetail>>(`${this.baseUrl}/cash-register-detail?${params}`);
    }

    totales(params: string): Observable<TotalMovimientos> {
        return this.http.get<TotalMovimientos>(`${this.baseUrl}/cash-register-detail/operations/totales?${params}`);
    }

    openPdf(idCaja, params, options): Observable<any> {
        return new Observable<any>(observer => {
            const url = `${environment.endpointApi}/public/pdf/cash-register/${idCaja}?${params}`;
            this.http.get(url, {responseType: 'blob'}).subscribe(blob => {
                promiseBlob2Data(blob).subscribe(data => {
                    const response: any = {
                        urlViewer: this.sanitizer.bypassSecurityTrustResourceUrl('/assets/pdfjs/web/viewer.html?file=' + data.objectUrl),
                        url: data.objectUrl,
                        originalUrl: url,
                        urlSafe: this.sanitizer.bypassSecurityTrustResourceUrl(url),
                        base64: data.base64
                    };
                    if (options.escPos) {
                        this.coffeEscPosService.cashRegisterEscPos(idCaja, params, options).subscribe(escPos => {
                            response.escPos = escPos;
                            observer.next(response);
                            observer.complete();
                        });
                    } else {
                        observer.next(response);
                        observer.complete();
                    }
                });
            });
        });
    }

    openPdfDetail(cashRegisterDetail: CashRegisterDetail, options: any): Observable<any> {
        const url = this.getUrlPdfDetail(cashRegisterDetail.id);
        return new Observable<any>(observer => {
            this.http.get(url, {responseType: 'blob'}).subscribe(blob => {
                promiseBlob2Data(blob).subscribe(data => {
                    const response: any = {
                        urlViewer: this.sanitizer.bypassSecurityTrustResourceUrl('/assets/pdfjs/web/viewer.html?file=' + data.objectUrl),
                        url: data.objectUrl,
                        originalUrl: url,
                        urlSafe: this.sanitizer.bypassSecurityTrustResourceUrl(url),
                        base64: data.base64
                    };
                    if (options.escPos) {
                        if (options.clienteImpresion === 'COFFE') {
                            this.coffeEscPosService.cashRegisterDetailEscPos(cashRegisterDetail.id, options).subscribe(escPos => {
                                response.escPos = escPos;
                                observer.next(response);
                                observer.complete();
                            });
                        } else {
                            observer.next(response);
                            observer.complete();
                        }
                    } else {
                        observer.next(response);
                        observer.complete();
                    }
                });
            });
        });
    }

    findDetailsByFormaPago(params: string): Observable<any> {
        return this.http.get<any>(`${this.baseUrl}/cash-register-detail/by-forma-pago?${params}`);
    }

    getTotalesMetodoPago(idCaja: number): Observable<any[]> {
        return this.http.get<any[]>(`${this.baseUrl}/cash-register-detail/operations/totales-metodo-pago?idCaja=${idCaja}`,
            {headers: {hideLoader: 'true'}});
    }

    deleteDetail(id) {
        return this.http.delete<void>(`${this.baseUrl}/cash-register-detail/${id}`);
    }

    getTrackingByDetailId(id: number): Observable<CashRegisterDetailTracking[]> {
        return this.http.get<CashRegisterDetailTracking[]>(`${this.baseUrl}/cash-register-detail/${id}/tracking`);
    }

    getUrlPdfDetail(id) {
        return `${environment.endpointApi}/public/pdf/cash-register-detail/${id}`;
    }
}
